// SKU Lab — see every detection a model makes, before the confidence floor removes most of them.
//
// The argument this page ends: a shelf shows 40 facings, compliance reports 22, and nobody can say
// whether the model was blind to the other 18 or saw them and the threshold discarded them. Those
// are opposite problems with opposite fixes — training data versus a number in a config — and the
// team has had no way to tell them apart.
//
// So the floor is dropped to ~0 and EVERY box comes back with its score. The production line is
// drawn across the results, which turns a flat list of detections into the sentence that matters:
// "these N would count today, these M are sitting just underneath it".
//
// The hover behaviour is the other half. A stocked chiller returns well over a hundred boxes at a
// low floor, and a hundred overlapping rectangles is not readable by anyone. Hovering a box — or
// its table row — dims everything else and magnifies that one facing, so a dense shelf can be read
// one product at a time.
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  Card, Select, Button, Upload, Table, Tag, Typography, Space, Slider, Empty,
  Alert, Statistic, Row, Col, Spin, message, Switch, Tooltip,
} from "antd";
import {
  InboxOutlined, ExperimentOutlined, AimOutlined, InfoCircleOutlined,
} from "@ant-design/icons";
import { getSkuLabModels, runSkuLabDetect } from "../network/api";

const { Text, Title } = Typography;
const { Dragger } = Upload;

// Colour by confidence, not by class. With 70+ classes a per-class palette is noise; what the
// reader is actually scanning for is "which of these would survive the floor".
const scoreColor = (score, floor) => {
  if (floor != null && score < floor) return "#d4380d"; // discarded today
  if (score >= 0.6) return "#237804";                   // comfortably above
  return "#ad6800";                                     // above, but not by much
};

const pct = (v) => `${(Number(v) * 100).toFixed(1)}%`;

export default function SkuLab() {
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState(null);
  const [floorConf, setFloorConf] = useState(0.01);
  const [conf, setConf] = useState(0.01);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [showBelowOnly, setShowBelowOnly] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  const imgWrapRef = useRef(null);
  const [rendered, setRendered] = useState({ w: 0, h: 0 });

  useEffect(() => {
    let alive = true;
    getSkuLabModels()
      .then((d) => {
        if (!alive) return;
        setModels(d.models || []);
        if (d.floorConf != null) {
          setFloorConf(d.floorConf);
          setConf(d.floorConf);
        }
      })
      .catch((e) =>
        setLoadErr(e?.response?.data?.message || e.message || "Could not load the model list"),
      );
    return () => { alive = false; };
  }, []);

  // Revoke the object URL when the picked file changes or the page unmounts — otherwise every
  // upload in a long session leaks a full-resolution frame.
  useEffect(() => {
    if (!file) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // The overlay is absolutely positioned over the rendered <img>, which is scaled to fit the
  // column. Boxes arrive in ORIGINAL image pixels, so every coordinate needs the current display
  // scale — and that changes with the window, not just with the image.
  const measure = useCallback(() => {
    const el = imgWrapRef.current?.querySelector("img");
    if (el) setRendered({ w: el.clientWidth, h: el.clientHeight });
  }, []);
  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, preview, result]);

  const run = async () => {
    if (!file || !modelId) return;
    setLoading(true);
    setHovered(null);
    try {
      const d = await runSkuLabDetect({ file, modelId, conf });
      setResult(d);
      if (!d.count) message.info("The model returned no detections at all on this image.");
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Detection failed");
    } finally {
      setLoading(false);
    }
  };

  const floor = result?.productionConf ?? null;
  const scale = result?.imageSize?.w ? rendered.w / result.imageSize.w : 0;

  const visible = useMemo(() => {
    const all = result?.boxes || [];
    return showBelowOnly && floor != null ? all.filter((b) => b.score < floor) : all;
  }, [result, showBelowOnly, floor]);

  const columns = [
    {
      title: "#", dataIndex: "i", width: 54,
      render: (i) => <Text type="secondary" style={{ fontSize: 12 }}>{i + 1}</Text>,
    },
    { title: "SKU", dataIndex: "label", ellipsis: true },
    { title: "Class", dataIndex: "cls", width: 70,
      render: (c) => <Text type="secondary" style={{ fontSize: 12 }}>{c}</Text> },
    {
      title: "Confidence", dataIndex: "score", width: 150,
      defaultSortOrder: "descend",
      sorter: (a, b) => a.score - b.score,
      render: (s) => (
        <Space size={6}>
          <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600,
                         color: scoreColor(s, floor) }}>{pct(s)}</span>
          {floor != null && s < floor && <Tag color="red" style={{ marginInlineEnd: 0 }}>below</Tag>}
        </Space>
      ),
    },
    {
      title: "Box", key: "box", width: 190,
      render: (_, b) => (
        <Text type="secondary" style={{ fontSize: 11, fontFamily: "monospace" }}>
          {Math.round(b.x1)},{Math.round(b.y1)} → {Math.round(b.x2)},{Math.round(b.y2)}
        </Text>
      ),
    },
  ];

  const selectedModel = models.find((m) => m.id === modelId);

  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginBottom: 4 }}>
        <ExperimentOutlined /> SKU Lab
      </Title>
      <Text type="secondary">
        Run any registered model over an image with the confidence floor switched off, and see
        every box it produced — including the ones production discards.
      </Text>

      {loadErr && <Alert type="error" showIcon style={{ marginTop: 12 }} message={loadErr} />}

      <Card size="small" style={{ marginTop: 14 }}>
        <Row gutter={[14, 14]} align="bottom">
          <Col xs={24} md={8}>
            <Text strong style={{ fontSize: 12 }}>Model</Text>
            <Select
              id="sku-lab-model"
              style={{ width: "100%", marginTop: 4 }}
              placeholder="Choose a model"
              value={modelId}
              onChange={setModelId}
              showSearch
              optionFilterProp="label"
              options={models.map((m) => ({
                value: m.id,
                label: `${m.id}${m.numClasses ? ` · ${m.numClasses} classes` : ""}`,
              }))}
            />
            {selectedModel && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {selectedModel.program || "no program"} · {selectedModel.task}
                {selectedModel.loaded ? " · loaded" : " · loads on first use"}
              </Text>
            )}
          </Col>
          <Col xs={24} md={10}>
            <Text strong style={{ fontSize: 12 }}>
              Detection floor: <span style={{ fontVariantNumeric: "tabular-nums" }}>{pct(conf)}</span>{" "}
              <Tooltip title="How low the model is allowed to go. The engine will not go below this — at exactly zero it emits a box per anchor and the result is unreadable.">
                <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
              </Tooltip>
            </Text>
            {/* A Slider's end marks are centred on the track ends, so their labels overhang the
                track by half their width — at md the leftmost one ("everything") lands on top of
                the Model picker in the column beside it. Padding the container in by the widest
                half-label keeps every mark inside its own column at any width. */}
            <div style={{ padding: "0 34px" }}>
              <Slider
                min={floorConf}
                max={0.6}
                step={0.01}
                value={conf}
                onChange={setConf}
                tooltip={{ formatter: pct }}
                marks={{ [floorConf]: "everything", 0.25: "compliance", 0.5: "share" }}
              />
            </div>
          </Col>
          <Col xs={24} md={6}>
            <Button type="primary" block size="large" loading={loading}
                    disabled={!file || !modelId} onClick={run}>
              {loading ? "Detecting…" : "Detect"}
            </Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={[14, 14]} style={{ marginTop: 14 }}>
        <Col xs={24} lg={14}>
          <Card size="small" title="Image" bodyStyle={{ padding: 10 }}>
            {!preview ? (
              <Dragger
                accept="image/*"
                multiple={false}
                maxCount={1}
                beforeUpload={(f) => { setFile(f); setResult(null); return false; }}
                showUploadList={false}
                style={{ padding: 20 }}
              >
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">Drop a chiller photo here, or click to choose</p>
                <p className="ant-upload-hint">Nothing is stored — the image is deleted once the boxes are computed.</p>
              </Dragger>
            ) : (
              <>
                <div
                  ref={imgWrapRef}
                  style={{ position: "relative", lineHeight: 0, background: "#f5f5f5" }}
                  onMouseLeave={() => setHovered(null)}
                >
                  <img
                    src={preview}
                    alt="uploaded frame"
                    onLoad={measure}
                    style={{ width: "100%", height: "auto", display: "block" }}
                  />
                  {/* Dim the frame while one box is focused, so the hovered facing is the only
                      thing at full contrast. Sits under the boxes, above the photo. */}
                  {hovered != null && (
                    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)",
                                  pointerEvents: "none", transition: "opacity .12s" }} />
                  )}
                  {scale > 0 && visible.map((b) => {
                    const isHot = hovered === b.i;
                    const left = b.x1 * scale, top = b.y1 * scale;
                    const w = (b.x2 - b.x1) * scale, h = (b.y2 - b.y1) * scale;
                    const colour = scoreColor(b.score, floor);
                    return (
                      <div
                        key={b.i}
                        onMouseEnter={() => setHovered(b.i)}
                        style={{
                          position: "absolute", left, top, width: w, height: h,
                          border: `${isHot ? 2.5 : 1.5}px solid ${colour}`,
                          borderRadius: 2,
                          // The focused box lifts above the dimming layer and scales up slightly —
                          // enough to read a 27px-wide 200ml facing without a separate zoom pane.
                          transform: isHot ? "scale(1.6)" : "none",
                          transformOrigin: "center",
                          boxShadow: isHot ? `0 0 0 9999px rgba(0,0,0,0)` : "none",
                          background: isHot ? "rgba(255,255,255,0.08)" : "transparent",
                          zIndex: isHot ? 5 : 2,
                          cursor: "crosshair",
                          transition: "transform .12s, border-width .12s",
                        }}
                      >
                        {isHot && (
                          <div style={{
                            position: "absolute", top: -18, left: 0, whiteSpace: "nowrap",
                            background: colour, color: "#fff", fontSize: 9, lineHeight: "14px",
                            padding: "0 4px", borderRadius: 2, fontWeight: 600,
                          }}>
                            {b.label} · {pct(b.score)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <Space style={{ marginTop: 8 }} wrap>
                  <Button size="small" onClick={() => { setFile(null); setResult(null); }}>
                    Choose another image
                  </Button>
                  {result && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      <AimOutlined /> hover a box, or a table row, to focus it
                    </Text>
                  )}
                </Space>
              </>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card size="small" title="Detections" bodyStyle={{ padding: 10 }}>
            {loading && <div style={{ padding: 40, textAlign: "center" }}><Spin /></div>}
            {!loading && !result && <Empty description="Run a detection to see every box and its confidence" />}
            {!loading && result && (
              <>
                <Row gutter={8}>
                  <Col span={8}><Statistic title="Detected" value={result.count} /></Col>
                  <Col span={8}>
                    <Statistic title="Would count" value={result.wouldCount ?? "—"}
                               valueStyle={{ color: "#237804" }} />
                  </Col>
                  <Col span={8}>
                    <Statistic title="Below the floor" value={result.wouldDiscard ?? "—"}
                               valueStyle={{ color: "#d4380d" }} />
                  </Col>
                </Row>

                {floor != null && (
                  <Alert
                    type={result.wouldDiscard ? "warning" : "success"}
                    showIcon
                    style={{ marginTop: 10 }}
                    message={
                      result.wouldDiscard
                        ? `${result.wouldDiscard} of ${result.count} detections sit below the ${pct(floor)} production floor`
                        : `Every detection clears the ${pct(floor)} production floor`
                    }
                    description={
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {result.productionConfSource}. This is a reference line, not a filter — the
                        table below is unfiltered.
                      </Text>
                    }
                  />
                )}

                <Space style={{ margin: "10px 0" }} wrap>
                  <Switch size="small" checked={showBelowOnly} onChange={setShowBelowOnly}
                          disabled={floor == null} />
                  <Text style={{ fontSize: 12 }}>Show only what production discards</Text>
                </Space>

                <Table
                  size="small"
                  rowKey="i"
                  dataSource={visible}
                  columns={columns}
                  pagination={{ pageSize: 12, size: "small", showSizeChanger: false }}
                  scroll={{ x: 520 }}
                  onRow={(b) => ({
                    onMouseEnter: () => setHovered(b.i),
                    onMouseLeave: () => setHovered(null),
                    style: hovered === b.i ? { background: "#e6f4ff" } : undefined,
                  })}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {result.modelId} · asked for ≥ {pct(result.conf)}
                  {result.timings?.total ? ` · ${Math.round(result.timings.total)} ms` : ""}
                </Text>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
